import { useState, useCallback, useEffect, Fragment } from 'react';
import DateRangePicker from '../../components/DateRangePicker';
import API from '../../services/api';
import Loading from '../../components/Loading';
import { twMerge } from 'tailwind-merge';
import { formatNumber } from '../../utils/NumberUtils';

const WorkReport = () => {
  const [range, setRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  });
  const [summary, setSummary] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const handleReport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await API.report.getWorkReport({
        startDate: range.from,
        endDate: range.to,
      });
      setSummary(response.data?.summary);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [range]);

  useEffect(() => {
    handleReport();
  }, [handleReport]);

  const thClass =
    'sticky top-0 px-2 py-3 text-left text-sm whitespace-nowrap font-bold uppercase tracking-wide text-gray-900';
  const tdClass = 'py-1 px-2 whitespace-nowrap';

  const renderTable = () => {
    if (!summary) return null;
    return (
      <div className="h-[calc(100vh-200px)] mt-2 overflow-auto border-t border-gray-200">
        <table className="w-full border-none">
          <thead className="sticky top-0 bg-white shadow-sm">
            <tr className="bg-gray-200">
              <th scope="col" className={twMerge(thClass, 'pl-2')}>
                Entity
              </th>
              <th scope="col" className={thClass}>
                Client
              </th>
              <th scope="col" className={thClass}>
                Project Code
              </th>
              <th scope="col" className={thClass}>
                Project
              </th>
              <th scope="col" className={thClass}>
                Employee
              </th>
              <th scope="col" className={thClass}>
                Hours
              </th>
              <th scope="col" className={thClass}>
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {summary.map((entity, entityIndex) => {
              return (
                <Fragment key={entityIndex}>
                  <tr className="bg-blue-50">
                    <td
                      className={twMerge(
                        tdClass,
                        'pl-2 font-bold text-blue-600',
                      )}
                    >
                      {entity.entity_name}
                    </td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td className={twMerge(tdClass, 'font-bold text-blue-600')}>
                      {formatNumber(entity.total_hours)}
                    </td>
                  </tr>
                  {entity.companies.map((company, companyIndex) => {
                    return (
                      <Fragment key={companyIndex}>
                        <tr>
                          <td />
                          <td
                            className={twMerge(
                              tdClass,
                              'font-bold bg-green-50 text-green-500 text-sm',
                            )}
                          >
                            {company.company_name || 'Unknown'}
                          </td>
                          <td className="bg-green-50" />
                          <td className="bg-green-50" />
                          <td className="bg-green-50" />
                          <td className="bg-green-50" />
                          <td
                            className={twMerge(
                              tdClass,
                              'font-bold bg-green-50 text-green-500 text-sm',
                            )}
                          >
                            {formatNumber(company.total_hours)}
                          </td>
                        </tr>
                        {company.projects.map((project, projectIndex) => {
                          return (
                            <Fragment key={projectIndex}>
                              <tr>
                                <td />
                                <td />
                                <td
                                  className={twMerge(
                                    tdClass,
                                    'font-bold bg-red-50 text-red-500 text-sm',
                                  )}
                                >
                                  {project.project_code || 'Unknown'}
                                </td>
                                <td
                                  className={twMerge(
                                    tdClass,
                                    'font-bold bg-red-50 text-red-500 text-sm',
                                  )}
                                >
                                  {project.project_name || 'Unknown'}
                                </td>
                                <td className="bg-red-50" />
                                <td className="bg-red-50" />
                                <td
                                  className={twMerge(
                                    tdClass,
                                    'font-bold bg-red-50 text-red-500 text-sm',
                                  )}
                                >
                                  {formatNumber(project.total_hours)}
                                </td>
                              </tr>
                              {project.employees.map(
                                (employee, employeeIndex) => {
                                  return (
                                    <tr key={employeeIndex}>
                                      <td />
                                      <td />
                                      <td />
                                      <td />
                                      <td
                                        className={twMerge(
                                          tdClass,
                                          'font-bold bg-gray-50 text-gray-500 text-sm',
                                        )}
                                      >
                                        {employee.employee_name}
                                      </td>
                                      <td
                                        className={twMerge(
                                          tdClass,
                                          'font-bold bg-gray-50 text-gray-500 text-sm',
                                        )}
                                      >
                                        {formatNumber(employee.total_hours)}
                                      </td>
                                      <td className="bg-gray-50" />
                                    </tr>
                                  );
                                },
                              )}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const totalHours =
    summary?.reduce((acc, project) => acc + project.total_hours, 0) || 0;

  return (
    <div className="container mx-auto bg-white">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Report</h1>
        <div className="flex flex-row gap-4">
          <DateRangePicker range={range} setRange={setRange} />
        </div>
      </div>
      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <div className="w-10 h-10">
            <Loading />
          </div>
        </div>
      )}
      {!summary && !isLoading && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600">No data found for this range.</p>
        </div>
      )}
      {summary && !isLoading && (
        <>
          <div className="flex justify-end">
            <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600">
                Total hours: {formatNumber(totalHours)}
              </p>
            </div>
          </div>
          {renderTable()}
        </>
      )}
    </div>
  );
};

export default WorkReport;
